import React, { lazy, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { withConsumer } from "./helpers/CacheContext";
import Loader from "./components/Loader";

const PageListItem = lazy(() =>
  import("./components/PageListItem/PageListItem")
);
const PageContent = lazy(() => import("./components/PageContent"));
const PageList = lazy(() => import("./components/PageList"));
const Einrichtungen = lazy(() => import("./components/Einrichtungen"));

const Routing = (props) => {
  const { fetchKirbyData, fetchSOALData } = props;
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setContent(null); // Reset content when path changes
    setError(false);
  }, [location.pathname]);

  useEffect(() => {
    const loadContent = async () => {
      const path = location.pathname.substring(1);
      const parents = path.split("/");
      const slug = parents.pop();

      let kirbyPageQuery = {
        query: `page('${parents.join("/") + "/" + slug}')`,
        select: {
          id: true,
          title: true,
          pagetype: true,
          resource: true,
          filterVariable: "page.filtervariable.value",
          offerimg: "page.offerimg.toFile",
          layout: {
            query: "page.editor.toLayouts",
            select: {
              columns: {
                query: "layout.columns",
                select: {
                  width: true,
                  span: true,
                  blocks: true,
                },
              },
            },
          },
          einrichtungsliste: "page.einrichtungsliste.toStructure",
          einrichtungslogos: {
            query: "page.einrichtungslogos.toStructure",
            select: { id: true, logo: "structureItem.logo.toFile" },
          },
          blocks: {
            query: "page.editor.toBlocks",
            select: {
              id: true,
              type: true,
              headline: true,
              name: "block.name.value",
              text: "block.text.value",
              background: "block.background",
              border: "block.border",
              html: "block.toHtml",
              table: "block.rows.toStructure",
              image: "block.image.toFile",
              color: "block.color",
              form: "block.form.value",
              formId: "block.formId.value",
              confirmstep: "block.confirmstep.toBool",
              alt: "block.alt",
              block: "block.block.toBool",
              link: "block.link",
              caption: "block.caption",
              images: {
                query: "block.images.toFiles",
                select: {
                  url: true,
                },
              },
            },
          },
          description: "page.description.kirbytext",
          sitebar: {
            query: "page.sitebar.toBlocks",
            select: {
              id: true,
              type: true,
              headline: true,
              padding: "block.padding.toBool",
              subline: true,
              background: true,
              variant: "block.variant.value",
              name: "block.name.value",
              text: "block.text.kirbytext",
              table: "block.rows.toStructure",
              html: "block.toHtml",
              kategorie: "block.kategorie.value",
              item: "block.item.value",
              image: "block.image.toFile",
              caption: "block.caption",
              links: {
                query: "block.links.toStructure",
                select: {
                  href: "structureItem.href.toUrl",
                  page: "structureItem.href.value",
                  label: "structureItem.label.value",
                },
              },
              images: {
                query: "block.images.toFiles",
                select: {
                  url: true,
                },
              },
            },
          },
          subpages: {
            query: "page.children",
            select: {
              id: true,
              title: true,
              pagetype: true,
              description: "page.description.value",
              offerimg: "page.offerimg.toFile",
              pagelink: true,
              category: "page.category.value",
              date: "page.date.toDate('d.m.Y')",
            },
          },
        },
      };

      // Adjust query for parent pages that use API-based list items
      const isApiListPage =
        parents.length > 0 &&
        (parents[0] === "termine" ||
          parents[0] === "jobboerse" ||
          parents[0] === "archiv" ||
          parents[0] === "weiterbildungen" ||
          parents[0] === "webi");

      if (isApiListPage) {
        kirbyPageQuery.query = `page('${parents[0]}')`;
      }

      try {
        const kirbyData = await fetchKirbyData(kirbyPageQuery);
        const { result } = kirbyData;

        if (result.pagetype === "list") {
          for (let i = 0; i < result.subpages.length; i++) {
            if (result.subpages[i].id === path) {
              kirbyPageQuery.query = `page('${path}')`;
              fetchKirbyData(kirbyPageQuery).then((data) =>
                setContent(data.result)
              );
              return;
            }
          }

          let listItems = await fetchSOALData(result.resource);

          // Team-Daten sind verschachtelt (Abteilung -> Mitglieder), flach machen
          if (result.resource === "/team" && listItems) {
            listItems = Object.values(listItems).flatMap((group) =>
              Object.values(group)
            );
          }

          const updatedData = { ...result, items: listItems };

          // Find the specific list item
          if (listItems) {
            const item = Object.values(listItems).find(
              (i) =>
                (i.permalink && i.permalink === slug) ||
                (i.id && i.id.toString() === slug) ||
                (i.seminarnummer &&
                  i.seminarnummer.toLowerCase() === slug.toLowerCase())
            );

            if (item) {
              setContent({
                item,
                pagetype: "listitem",
                title: updatedData.title,
                path: updatedData.path,
                sitebar: updatedData.sitebar,
                image: updatedData.image,
                listContent: updatedData.listtexts,
              });
              return;
            }

            // Item not found (e.g. not yet published) → show full parent list page
            // For webi (short URL), load weiterbildungen page instead (has sitebar)
            if (parents[0] === "webi") {
              kirbyPageQuery.query = `page('weiterbildungen')`;
              const fallbackData = await fetchKirbyData(kirbyPageQuery);
              const fallbackResult = fallbackData.result;
              setContent({ ...fallbackResult, items: listItems });
              return;
            }
          }
          setContent(updatedData);
        } else {
          kirbyPageQuery.query = `page('${parents.join("/") + "/" + slug}')`;
          const kirbyData = await fetchKirbyData(kirbyPageQuery);

          setContent(kirbyData.result); // Setze Content auf die normale Seite

          if (result.pagetype === "blog") {
            const subFilter = [
              ...new Set(result.subpages.map((page) => page.category)),
            ];

            setContent({
              ...result,
              items: result.subpages,
              subFilter,
              filterVariable: "category",
              resource: "blog",
            });
            return;
          }
          return;
        }
      } catch (error) {
        console.error("Routing error:", error);
        setError(true);
      }
    };

    loadContent();
  }, [fetchKirbyData, fetchSOALData, location.pathname]);

  if (error) {
    return (
      <div className="page">
        <div className="page-block">
          <div className="page-block-big">
            <div className="page-block--content is-full">
              <div className="page-block--content-headline">
                Seite konnte nicht geladen werden
              </div>
              <p>
                Bitte versuchen Sie es später erneut oder kehren Sie zur{" "}
                <a href="/">Startseite</a> zurück.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return <Loader />;
  }

  // Render based on the page type

  switch (content.pagetype) {
    case "einrichtungen":
      return (
        <Einrichtungen
          einrichtungen={content.einrichtungsliste}
          logos={content.einrichtungslogos}
          content={content}
        />
      );
    case "list":
    case "blog":
      return <PageList content={content} location={location} />;
    case "listitem":
      return (
        <PageListItem
          content={content}
          location={location}
          sitebar={content.sitebar}
          image={content.image}
          listContent={content.listContent}
        />
      );
    case "content":
    case "article":
      return <PageContent content={content} location={location} />;
    default:
      return <Loader />;
  }
};

export default withConsumer(Routing);
